// ─── snapshot.js ─────────────────────────────────────────────────────────────
// Captura del frame actual de una cell de video (WebRTC, HLS o MJPEG), con
// watermark de cadena de custodia, descarga local inmediata y upload opcional
// al SensorStore como evento manual.
//
// Uso típico:
//   const blob = await captureCellFrame(cellEl, {
//     siteName: 'Hites Alameda',
//     cameraName: 'CH03 · Calle Latadia',
//     operatorName: 'paulohites@retaliq.net',
//   })
//   downloadBlob(blob, makeSnapshotFilename({ siteName, cameraName }))
//
// Watermark cubre los 4 bordes del PNG con info legal: sitio, cámara, fecha
// de generación, operador y hash corto del blob (cadena de custodia básica).
// Eso permite que ese PNG sea entregable como evidencia preliminar.

function pad2(n) { return String(n).padStart(2, '0') }
function pad3(n) { return String(n).padStart(3, '0') }

export function nowSlug(d = new Date()) {
  return `${d.getFullYear()}${pad2(d.getMonth()+1)}${pad2(d.getDate())}-${pad2(d.getHours())}${pad2(d.getMinutes())}${pad2(d.getSeconds())}`
}

export function nowIsoLocal(d = new Date()) {
  return `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}.${pad3(d.getMilliseconds())}`
}

function safeSlug(s) {
  return String(s || 'sin-nombre')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9_\-]+/g, '_')
    .replace(/_+/g, '_').replace(/^_|_$/g, '')
    .toLowerCase()
}

export function makeSnapshotFilename({ siteName, cameraName, ts = new Date() }) {
  return `${safeSlug(siteName)}__${safeSlug(cameraName)}__${nowSlug(ts)}.png`
}

// Busca el primer elemento <video> o <img> con contenido visible dentro de un
// nodo. Si la cell tiene varios (placeholder + real), prefiere el que tenga
// frames realmente cargados.
function findMediaInside(node) {
  if (!node) return null
  const videos = [...node.querySelectorAll('video')]
  const v = videos.find(v => v.videoWidth > 0 && v.videoHeight > 0) || videos[0] || null
  if (v && v.videoWidth > 0) return { kind: 'video', el: v, w: v.videoWidth, h: v.videoHeight }
  const imgs = [...node.querySelectorAll('img')]
  const i = imgs.find(i => i.naturalWidth > 0 && i.naturalHeight > 0) || imgs[0] || null
  if (i && i.naturalWidth > 0) return { kind: 'img', el: i, w: i.naturalWidth, h: i.naturalHeight }
  return null
}

// Dibuja un texto con caja oscura translúcida. Útil para que el watermark sea
// legible sobre fondo claro u oscuro.
function drawTextWithBg(ctx, text, x, y, opts = {}) {
  const {
    font = '14px system-ui, -apple-system, sans-serif',
    fg   = '#ffffff',
    bg   = 'rgba(0,0,0,0.7)',
    padX = 8, padY = 4,
    align = 'left',
    baseline = 'top',
  } = opts
  ctx.font = font
  ctx.textBaseline = baseline
  const m = ctx.measureText(text)
  const w = Math.ceil(m.width) + padX * 2
  const fontSize = parseInt(font.match(/(\d+)px/)?.[1] || '14', 10)
  const h = fontSize + padY * 2
  let bx = x
  if (align === 'right') bx = x - w
  if (align === 'center') bx = x - w / 2
  let by = y
  if (baseline === 'bottom') by = y - h
  ctx.fillStyle = bg
  ctx.fillRect(bx, by, w, h)
  ctx.fillStyle = fg
  ctx.textAlign = 'left'
  ctx.fillText(text, bx + padX, by + padY)
}

// Hash corto (8 chars hex) del blob — para cadena de custodia básica.
// SubtleCrypto está disponible en https://, suficiente para este caso.
async function shortHash(blob) {
  try {
    const buf = await blob.arrayBuffer()
    const digest = await crypto.subtle.digest('SHA-256', buf)
    return [...new Uint8Array(digest)].slice(0, 4).map(b => b.toString(16).padStart(2, '0')).join('')
  } catch {
    return '--------'
  }
}

// Captura el frame del media dentro de cellEl y lo devuelve como Blob PNG con
// watermark de cadena de custodia.
//
// meta = { siteName, cameraName, operatorName }
// Cualquier campo faltante se reemplaza por un texto neutro.
export async function captureCellFrame(cellEl, meta = {}) {
  const found = findMediaInside(cellEl)
  if (!found) throw new Error('No hay video/imagen activo en la celda')

  const { el, w, h } = found
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  try {
    ctx.drawImage(el, 0, 0, w, h)
  } catch (e) {
    // CORS-tainted (MJPEG cross-origin). En principio el MJPEG viene del mismo
    // origen (mismo /api/), pero por las dudas reportamos error claro.
    throw new Error('Frame protegido por CORS — no se pudo capturar (' + e.message + ')')
  }

  // Capa de watermark — 4 esquinas.
  const site = meta.siteName    || 'Sitio desconocido'
  const cam  = meta.cameraName  || 'Cámara desconocida'
  const op   = meta.operatorName|| 'operador'
  const tsIso = nowIsoLocal()

  // Banda superior izquierda — sitio + cámara
  drawTextWithBg(ctx, `${site} · ${cam}`, 12, 12, {
    font: 'bold 16px system-ui, -apple-system, sans-serif',
    bg: 'rgba(0,0,0,0.72)', fg: '#ffffff',
  })
  // Banda superior derecha — timestamp generación
  drawTextWithBg(ctx, `Capturado ${tsIso}`, w - 12, 12, {
    font: '13px system-ui, -apple-system, sans-serif',
    align: 'right', bg: 'rgba(0,0,0,0.72)', fg: '#ffffff',
  })
  // Banda inferior izquierda — marca RetaliQ
  drawTextWithBg(ctx, 'RetaliQ CAD · Evidencia preliminar', 12, h - 12, {
    font: '12px system-ui, -apple-system, sans-serif',
    bg: 'rgba(0,0,0,0.72)', fg: '#ffffff', baseline: 'bottom',
  })
  // Banda inferior derecha — operador
  drawTextWithBg(ctx, `Operador: ${op}`, w - 12, h - 12, {
    font: '12px system-ui, -apple-system, sans-serif',
    align: 'right', baseline: 'bottom', bg: 'rgba(0,0,0,0.72)', fg: '#ffffff',
  })

  return new Promise((resolve, reject) => {
    canvas.toBlob(async (blob) => {
      if (!blob) { reject(new Error('canvas.toBlob falló')); return }
      // Después del primer toBlob agregamos un hash en otra esquina si quisiéramos
      // re-firmar. Para no re-encodear, devolvemos el blob tal cual y dejamos el
      // hash como metadata en la respuesta del upload.
      blob.shortHash = await shortHash(blob)
      blob.width = w
      blob.height = h
      blob.capturedAt = tsIso
      resolve(blob)
    }, 'image/png')
  })
}

// Descarga un blob como archivo en disco — usa <a download> oculto.
export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  setTimeout(() => {
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, 1000)
}

// POST opcional al backend para guardar el snapshot como evento manual en la
// galería forense. Sin bloquear: si falla, el archivo ya está descargado local.
export async function uploadSnapshotToSensorStore(blob, meta, token) {
  const fd = new FormData()
  fd.append('image', blob, makeSnapshotFilename(meta))
  fd.append('site_id', String(meta.siteId ?? ''))
  fd.append('channel', String(meta.channel ?? ''))
  fd.append('camera_name', meta.cameraName || '')
  fd.append('site_name', meta.siteName || '')
  fd.append('operator', meta.operatorName || '')
  fd.append('captured_at', blob.capturedAt || nowIsoLocal())
  fd.append('short_hash', blob.shortHash || '')
  fd.append('event_type', 'manual_snapshot')
  try {
    const r = await fetch('/api/sensors/manual_snapshot', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    })
    if (!r.ok) throw new Error(`upload ${r.status}: ${await r.text().catch(()=>'')}`)
    return await r.json()
  } catch (e) {
    // Silenciar — la descarga ya está en disco. Devolvemos null para que el
    // caller pueda decidir si avisa al usuario o no.
    console.warn('[snapshot] upload SensorStore falló:', e.message)
    return null
  }
}
